import React, { useState, useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";

import RemoteLayout from "./layout";
import { Conference } from "../../components/videoCalling";
import { WorkshopContext } from "../../contexts/workshop.context";
import { defaultRoles, generateStarPickerStats } from "../../util/workshop.util";

import { getLocalStorage, getNextRoute, getPreviousRoute } from "../../util/general.util";
import { StatsChart } from "../../components/insights";
import demoDataSet from "../../util/demoData.util";

const TASK_ID = "8c";

const Task8c = ({ demoWorkshop = false }) => {
  const { t } = useTranslation();

  const [isStarted, setIsStarted] = useState(false);
  const [starSelections, setStarSelections] = useState([]);

  const starsList = t("resources.starsList", { returnObjects: true });

  const {
    socket,
    user: { id, role } = {},
    workshopDetails: { platform } = {}
  } = useContext(WorkshopContext);

  useEffect(() => {
    if (demoWorkshop) {
      let starPosition = getLocalStorage("task8");
      if (!starPosition) {
        starPosition = demoDataSet[platform]?.startPositions;
      }
      const temp = generateStarPickerStats(starPosition, starsList);
      setStarSelections(temp);
      return;
    }
    socket.emit("getTaskActivityStatus", TASK_ID, (myTaskStatus) => {
      if (!myTaskStatus) return;
      setIsStarted(myTaskStatus?.ready);
    });

    socket.emit("getTaskStatus", 8, (starPositions) => {
      if (!starPositions) return;

      const temp = generateStarPickerStats(starPositions, starsList);
      setStarSelections(temp);
    });

    socket?.on("taskActivityStatus", (taskActivityStatus) => {
      const myTaskStatus = taskActivityStatus[TASK_ID];

      if (!myTaskStatus) return;
      if (myTaskStatus?.ready) setIsStarted(true);
    });
  }, [id]);

  const handleNext = () => {
    if (!isStarted) {
      socket.emit("taskActivityStatus", {
        taskId: TASK_ID,
        status: { ready: true }
      });
      setIsStarted(true);
      return;
    } else {
      const nextRoute = getNextRoute("/task-8/c", platform);
      socket.emit("navigate", nextRoute);
    }
  };

  const handleButtonClick = () => {
    if (role !== defaultRoles.participant) return handleNext;
    else return null;
  };

  const buttonText =
    role !== defaultRoles.participant && isStarted
      ? t("buttonNext") + " →"
      : role !== defaultRoles.participant && !isStarted
        ? t("task8.c.start") + " →"
        : null;

  const buttonColor =
    role !== defaultRoles.participant && isStarted
      ? "green"
      : role !== defaultRoles.participant && !isStarted
        ? "green"
        : "green";

  const handleBack = () => {
    const previousRoute = getPreviousRoute(`/task-8/b`, platform);
    socket.emit("navigate", previousRoute);
  };

  const showBackButton = role !== defaultRoles.participant && !isStarted;

  return (
    <RemoteLayout
      buttonText={buttonText}
      buttonColor={buttonColor}
      onButtonClick={handleButtonClick()}
      showParticipants={true}
      onBackClick={showBackButton && handleBack}>
      <div className="text-center mb-8">
        <h1 className="text-white text-2xl md:text-4xl font-bold flex items-center justify-center gap-2 text-white-100">
          <span className="text-yellow-500">🏆</span>{" "}
          {isStarted ? t("task8.c.started.title") : t("task8.c.title")}
        </h1>
        <p
          className="mt-4 max-w-2xl text-lg mx-auto text-white-100"
          dangerouslySetInnerHTML={{
            __html: isStarted ? t("task8.c.started.description") : t("task8.c.description")
          }}
        />
      </div>

      {/* Main Content */}
      {isStarted ? (
        <div className="flex flex-col md:items-start gap-4 w-full">
          <div className="w-full">
            <div className="bg-white-100 rounded-2xl p-4">
              <StatsChart data={starSelections} showSelectors={true} halfCol={true} />
            </div>
          </div>

          <div className="overflow-y-auto mt-4 w-full">
            <Conference isLinear={true} demoWorkshop={demoWorkshop} />
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12 w-full">
          {/* Task Overview Card */}
          <div className="bg-orange-50 text-black p-6 rounded-2xl shadow-2xl col-span-1 w-full h-fit">
            <h2 className="text-orange-default font-bold text-xl mb-4">
              {t("task8.c.card.title")}
            </h2>
            <ul
              className="list-inside"
              dangerouslySetInnerHTML={{ __html: t("task8.c.card.list.item1") }}
            />
          </div>
          <div className="col-span-1 md:col-span-2 w-full">
            <Conference smallSpace={true} demoWorkshop={demoWorkshop} />
          </div>
        </div>
      )}
    </RemoteLayout>
  );
};

export default Task8c;
